import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export interface UnitManagementLookupItem {
  id?: string | number;
  value?: string | number;
  name?: string;
  label?: string;
  title?: string;
  code?: string;
  imei?: string;
  serial?: string;
  [key: string]: unknown;
}

export interface UnitManagementLookupResponse {
  response?: UnitManagementLookupItem[];
  data?: UnitManagementLookupItem[];
  speeds?: UnitManagementLookupItem[];
  [key: string]: unknown;
}

export interface UnitSearchParams {
  query: string;
  unitType: "DVC" | "FUESNSR" | string;
  distributorId?: string | number | null;
}

export interface InstallUnitPayload {
  vehicleDetail: Record<string, unknown>;
  deviceDetail: Record<string, unknown>;
  fuelTankAndUnit: Array<Record<string, unknown>>;
  insurance: Array<Record<string, unknown>>;
  accessories: Array<Record<string, unknown>>;
  consumableResources: Array<Record<string, unknown>>;
}

export interface InstallUnitResponse {
  device?: {
    unit?: {
      id?: string | number;
    };
    [key: string]: unknown;
  };
  vehicleDetails?: {
    id?: string | number;
    [key: string]: unknown;
  };
  clientId?: string | number;
  [key: string]: unknown;
}

export interface VehicleSearchItem {
  id?: string | number;
  licensePlate?: string;
  vin?: string;
  [key: string]: unknown;
}

export interface VehicleSearchParams {
  clientId: string | number;
  query: string;
}

export interface FuelSensorAssociation {
  id?: string | number;
  unit?: {
    imei?: string;
    serial?: string;
    quantity?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface VehicleDetailResponse {
  id?: string | number;
  fuelSensors?: FuelSensorAssociation[];
  device?: {
    serial?: string;
    unit?: { serial?: string; imei?: string; [key: string]: unknown };
    [key: string]: unknown;
  };
  unit?: { serial?: string; imei?: string; [key: string]: unknown };
  serial?: string;
  [key: string]: unknown;
}

export interface UninstallUnitPayload {
  adjusterId: string | number;
  clientId: string | number;
  description?: string;
  vehicleId: string | number;
}

export interface ChangeUnitPayload {
  adjusterId: string | number;
  clientId: string | number;
  comments?: string;
  vehicleId: string | number;
  newUnitId: string | number;
}

const getLookup = (
  url: string,
  params: Record<string, unknown> = {},
  language?: string,
) =>
  ApiService.get<UnitManagementLookupResponse>(url, {
    params,
    ...getLanguageHeaders(language),
  });

export const getVehicleMakeLookup = (
  params: Record<string, unknown> = {},
  language?: string,
) =>
  getLookup(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.VEHICLE_MAKE,
    params,
    language,
  );

export const getVehicleModelLookup = (
  makeId: string | number,
  params: Record<string, unknown> = {},
  language?: string,
) =>
  getLookup(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.VEHICLE_MODEL.replace(
      "{id}",
      String(makeId),
    ),
    params,
    language,
  );

export const getVehicleTypeLookup = (
  params: Record<string, unknown> = {},
  language?: string,
) =>
  getLookup(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.VEHICLE_TYPE,
    params,
    language,
  );

export const getMaxSpeedLookup = (
  params: Record<string, unknown> = {},
  language?: string,
) =>
  getLookup(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.MAX_SPEED,
    params,
    language,
  );

export const getAdjusterLookup = (
  params: Record<string, unknown> = {},
  language?: string,
) =>
  getLookup(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.ADJUSTER_LIST,
    params,
    language,
  );

export const getClientLookup = (
  params: Record<string, unknown> = {},
  language?: string,
) =>
  getLookup(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.CLIENT_LIST,
    params,
    language,
  );

export const searchUnitByImeiOrSerial = (
  { query, unitType, distributorId }: UnitSearchParams,
  language?: string,
) =>
  ApiService.get<UnitManagementLookupResponse | UnitManagementLookupResponse[]>(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.UNIT_SEARCH,
    {
      params: {
        idDistributor: distributorId,
        operationType: "RTINST",
        withoutBroken: true,
        unitType,
        query,
      },
      ...getLanguageHeaders(language),
    },
  );

export const installUnit = (
  payload: InstallUnitPayload,
  language?: string,
) =>
  ApiService.post<{ response: InstallUnitResponse }>(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.BASE,
    payload,
    {
      ...getLanguageHeaders(language),
    },
  );

export const searchVehicle = (
  { clientId, query }: VehicleSearchParams,
  language?: string,
) =>
  ApiService.get<UnitManagementLookupResponse>(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.LOOKUP.VEHICLE_SEARCH,
    {
      params: { clientId, query },
      ...getLanguageHeaders(language),
    },
  );

export const getVehicleById = (id: string | number, language?: string) =>
  ApiService.get<{ response: VehicleDetailResponse }>(
    `${ServicesUrlEndpoints.UNIT_MANAGEMENT.BASE}/${id}`,
    getLanguageHeaders(language),
  );

export const uninstallUnit = (
  payload: UninstallUnitPayload,
  language?: string,
) =>
  ApiService.post(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.UNINSTALL,
    payload,
    getLanguageHeaders(language),
  );

export const uninstallFuelSensor = (
  fuelSensorAssociationId: string | number,
  language?: string,
) =>
  ApiService.delete(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.UNINSTALL_FUEL_SENSOR.replace(
      "{id}",
      String(fuelSensorAssociationId),
    ),
    getLanguageHeaders(language),
  );

export const changeUnit = (payload: ChangeUnitPayload, language?: string) =>
  ApiService.patch(
    ServicesUrlEndpoints.UNIT_MANAGEMENT.BASE,
    payload,
    getLanguageHeaders(language),
  );
